import { rem, useToasts } from "@certn/cascadia";
import {
  BasicButton,
  Container,
  Grid,
  GridItem,
  Illustration,
  Text,
} from "@certn/cascadia-react";
import { ActionBar } from "@certn/cascadia-react/components/action-bar/ActionBar";
import OneIdImage from "@certn/cascadia-react/foundations/illustrations/OneIdImage";
import { request, useAPIRequest } from "@certn/legacy-api";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { MESSAGES, createVeriffFrame } from "@veriff/incontext-sdk";
import i18n from "i18next";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { SkipVerificationModal } from "./IdentityVerificationSkipModal";
import { ConfirmCloseTrustmaticModal } from "./Trustmatic/ConfirmCloseTrustmaticModal";
import { TrustmaticModal } from "./Trustmatic/TrustmaticModal";
import constants from "../../../../../constants";
import { ApplicationContext } from "../../Application.context";
import { SectionProps } from "../types";

const oneIDProviderMap = {
  TRUSTMATIC: "CERTN_ONEID",
  VERIFF: "ONEID1",
};

export type CreateSessionData = {
  src_url: string;
};

export type TrustmaticEventNotification = {
  hasCompleted: boolean;
  hasError: boolean;
  sessionId: string;
  error?: string;
};

type Content = {
  subtitle: string | undefined;
  image: string | React.ReactElement;
};

export function IdentityVerificationSection({
  section,
  onNext,
  onBack,
}: SectionProps) {
  const { t } = useTranslation();
  const { show } = useToasts();
  const [showSkipModal, setSkipModal] = useState(false);
  const { applicantId } = useParams();
  const enhancedIdentityInstance: any = section.fields[0].value;
  const [verificationSkip, setVerificationSkip] = useState(
    enhancedIdentityInstance.verification_status
  );

  const [openConfirmCloseTrustmaticModal, setOpenConfirmCloseTrustmaticModal] =
    useState(false);
  const [trustmaticModalOpen, setTrustmaticModalOpen] = useState(false);
  const [trustmaticUrlError, setTrustmaticUrlError] = useState<boolean>(false);
  const [trustmaticUrl, setTrustmaticUrl] = useState<string>("");
  const [trustmaticResponse, setTrustmaticResponse] = useState<
    TrustmaticEventNotification | undefined
  >(undefined);

  const [status, setStatus] = useState<
    components["schemas"]["OneIDCheckFieldValueStatusEnum"]
  >(enhancedIdentityInstance.status);
  const internalApiRequest = useAPIRequest();
  const { completeSection } = useContext(ApplicationContext);

  const isBypass = verificationSkip === "BYPASS";
  const isTrustmatic =
    enhancedIdentityInstance.provider === oneIDProviderMap["TRUSTMATIC"];

  const openTrustmaticModal = () => {
    setTrustmaticModalOpen(true);
  };

  const getTrustmaticLink = async () => {
    if (!isTrustmatic) return;
    if (section.isCompleted) return;

    setTrustmaticUrlError(false);
    const { src_url } = await internalApiRequest<CreateSessionData>(
      `/internal/people/applicant/${applicantId}/verification/create`,
      {
        method: "POST",
      }
    );

    if (src_url) {
      setTrustmaticUrl(src_url);
    } else {
      setTrustmaticUrlError(true);
    }
  };

  const enrollmentCompleted = (event: MessageEvent) => {
    const { data }: { data: TrustmaticEventNotification } = event;
    setTrustmaticResponse(data);
  };

  useEffect(() => {
    if (trustmaticModalOpen) {
      window.addEventListener("message", enrollmentCompleted, false);
    } else {
      window.removeEventListener("message", enrollmentCompleted, false);
    }
  }, [trustmaticModalOpen]);

  useEffect(() => {
    getTrustmaticLink();
  }, []);

  useEffect(() => {
    const handleTrustmaticCompletedEvent = () => {
      setTrustmaticModalOpen(false);
      handleSubmitVerification();
    };
    const handleTrustmaticErrorEvent = (
      _trustmaticResponse: TrustmaticEventNotification
    ) => {
      console.error("Error starting verification:", _trustmaticResponse.error);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    };

    if (trustmaticResponse?.hasCompleted) handleTrustmaticCompletedEvent();
    if (trustmaticResponse?.hasError)
      handleTrustmaticErrorEvent(trustmaticResponse);
  }, [trustmaticResponse]);

  const confirmCloseTrustmaticModal = () => {
    if (trustmaticModalOpen) {
      setOpenConfirmCloseTrustmaticModal(false);
      setTrustmaticModalOpen(false);
    }
  };

  const returnToTrustmaticModal = () => {
    setOpenConfirmCloseTrustmaticModal(false);
  };

  const closeTrustmaticModal = () => {
    trustmaticModalOpen && setOpenConfirmCloseTrustmaticModal(true);
  };

  const handleSubmitVerification = async () => {
    try {
      await internalApiRequest(
        `/internal/people/applicant/${applicantId}/verification/started`,
        {
          method: "POST",
        }
      );
      setStatus("P");
    } catch (e) {
      console.error("Error starting verification:", e);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
  };

  const handleSkipVerification = async () => {
    try {
      await internalApiRequest(
        `/internal/people/applicant/${applicantId}/verification/bypass`,
        {
          method: "POST",
        }
      );
      setVerificationSkip("BYPASS");
      setSkipModal(false);
    } catch (e) {
      console.error("Error bypassing verification:", e);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
  };

  const handleStartVeriffVerification = async () => {
    try {
      const response: any = await request("https://api.veriff.me/v1/sessions", {
        method: "POST",
        headers: {
          "X-AUTH-CLIENT": constants.VITE_VERIFF_PUBLIC_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          verification: { vendorData: enhancedIdentityInstance.id },
        }),
      });

      createVeriffFrame({
        url: response.verification.url,
        // Veriff only accepts en and fr as languages
        lang: i18n.language.split("-")[0],
        onEvent(msg) {
          switch (msg) {
            case MESSAGES.CANCELED:
              // Could be useful for exit modal
              break;
            case MESSAGES.FINISHED:
              handleSubmitVerification();
              break;
          }
        },
      });
    } catch (err) {
      console.error("Error during OneId Verification:", err);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
  };

  const handleDemoSkip = async () => {
    try {
      await internalApiRequest(
        `/internal/people/applicant/${applicantId}/verification/demo-skip`,
        {
          method: "POST",
        }
      );
      setStatus("P");
    } catch {
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }
  };

  const sectionContent: Record<"notStarted" | "complete" | "skipped", Content> =
    {
      notStarted: {
        subtitle: section.subtitle,
        image: <OneIdImage />,
      },
      complete: {
        subtitle: t(
          "Your verification details have been submitted. Please continue to the next step."
        ),
        image: <Illustration name="oneid-success" size={132} />,
      },
      skipped: {
        subtitle: t(
          "You have chosen to skip the verification process and one of our representatives will contact you to verify your identity after submission."
        ),
        image: <Illustration name="oneid-success" size={132} />,
      },
    };

  const getStatusContent: any = () => {
    if (isBypass) {
      return sectionContent.skipped;
    } else if (status === "N") {
      return sectionContent.notStarted;
    } else {
      return sectionContent.complete;
    }
  };

  return (
    <Container>
      <Grid aria-label={t("Identity Verification")}>
        <GridItem>
          <Text
            as="h1"
            style="form/page-title"
            className={css({
              marginTop: rem("space-6"),
            })}
          >
            {section.title}
          </Text>
          <Text
            style="form/body-primary"
            color="text-weak"
            className={css({ marginTop: rem("space-2") })}
          >
            {getStatusContent().subtitle}
          </Text>
        </GridItem>
        <GridItem alignItems="center">{getStatusContent().image}</GridItem>

        {constants.MODE !== "production" && status === "N" && (
          <GridItem>
            <BasicButton size="small" onPress={handleDemoSkip}>
              Demo Skip
            </BasicButton>
          </GridItem>
        )}
        <TrustmaticModal
          show={trustmaticModalOpen}
          onClose={closeTrustmaticModal}
          trustmaticUrl={trustmaticUrl}
          isError={trustmaticUrlError}
        />
        <ConfirmCloseTrustmaticModal
          show={openConfirmCloseTrustmaticModal}
          onClose={returnToTrustmaticModal}
          onExit={confirmCloseTrustmaticModal}
        />
        <GridItem>
          {status === "N" && !isBypass ? (
            <ActionBar
              primaryText={t("Start verification")}
              onPrimaryClick={
                isTrustmatic
                  ? openTrustmaticModal
                  : handleStartVeriffVerification
              }
              secondaryText={t("Skip")}
              onSecondaryClick={() => setSkipModal(true)}
              backText={t("Back")}
              onBackClick={onBack}
            />
          ) : (
            <ActionBar
              primaryText={t("Next step")}
              onPrimaryClick={() => {
                completeSection();
                onNext();
              }}
              backText={t("Back")}
              onBackClick={onBack}
            />
          )}
        </GridItem>
        <SkipVerificationModal
          show={showSkipModal}
          onClose={() => setSkipModal(false)}
          handleSkipVerification={handleSkipVerification}
        />
      </Grid>
    </Container>
  );
}
